import { useEffect, useId, useRef } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useGlobalHydrationActions } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';

/**
 * This component is rendered at all suspense boundaries (placeholders), to be able to know when
 * hydration has completed globally - when every subtree has rendered.
 */
const useHydrationSubtreeCompleterImpl = ({
	isSubtreeRendered,
}: {
	// True if the suspense's fallback or children component has completed rendering
	isSubtreeRendered: boolean;
}) => {
	const id = useId();
	const { addPendingSubtree, completePendingSubtree } = useGlobalHydrationActions();

	useEffect(() => {
		if (isSubtreeRendered) {
			completePendingSubtree(id);
		} else {
			addPendingSubtree(id);
		}
	}, [isSubtreeRendered, id, completePendingSubtree, addPendingSubtree]);

	// Exists to ensure that we complete a subtree if it unmounts before `isSubtreeRendered` flips to true
	useEffect(() => {
		return () => {
			completePendingSubtree(id);
		};
	}, [id, completePendingSubtree]);
};

export const useHydrationSubtreeCompleter = (params: { isSubtreeRendered: boolean }) => {
	// Feature gates flip between on/off in storybooks, resulting in React (rightly) complaining that the number of hook
	// calls has changed between renders. This specific hook is a no-op in storybooks anyway, so freeze the condition.
	const enabled = useRef(fg('jfp-magma-fix-hydration-completion')).current;
	if (enabled) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useHydrationSubtreeCompleterImpl(params);
	}
};
